import { useState } from "react";
import { constants } from "lib/utils/constants";
import { clearAnonymousId, fetchAnonymousId, readAnonymousId, writeAnonymousId } from "lib/utils/fingerprint";
import { useDebounceCallback } from "./useDebounceCallback";

interface UseAnonymousIdReturn {
  /** Current anonymous ID value from localStorage */
  anonymousId: string | null;
  /** Initialize anonymous ID - fetches from API if not already known */
  initializeAnonymousId: (signed?: boolean, authToken?: string) => Promise<string | null>;
  /** Forget that this device was mapped to a user, so the next login maps again */
  resetUserMapping: () => void;
  /** Drop the cached ID entirely; the next initialize re-resolves it from the service */
  resetAnonymousId: () => void;
}

/**
 * The anonymous ID identifies the browser on this device, not the person - the service recognises it
 * from the signals alone. So it survives login and logout, and only the "mapped to a user" marker is
 * cleared when someone logs out. Deleting the ID would achieve nothing anyway: the next resolve
 * returns the same one from the same signals, at the cost of a request.
 */
const useAnonymousId = (): UseAnonymousIdReturn => {
  const [anonymousId, setAnonymousId] = useState<string | null>(() => readAnonymousId());

  /**
   * Calls the service only when it has something to learn: no ID yet, or a logged-in shopper whose
   * device has not been mapped to them. Anything else is answered from localStorage - which matters
   * because map-user is a write path, resolving the device and upserting a row per near neighbour.
   */
  const initializeAnonymousId = useDebounceCallback(
    async (signed: boolean = false, authToken?: string): Promise<string | null> => {
      try {
        const existingId = readAnonymousId();
        const alreadyMapped = Boolean(localStorage.getItem(constants.LOCAL_STORAGE_FLO_ANON_ID_MAPPED));
        const needsMapping = signed && !alreadyMapped;

        if (existingId && !needsMapping) {
          setAnonymousId(existingId);
          return existingId;
        }

        const { fingerprintId, mapped } = await fetchAnonymousId(signed, authToken);
        if (fingerprintId) {
          writeAnonymousId(fingerprintId);
          setAnonymousId(fingerprintId);
          // Only when map-user actually ran. Signed with no token falls back to resolve, and marking
          // that as mapped would mean the real login never links the device to the person.
          if (mapped) {
            localStorage.setItem(constants.LOCAL_STORAGE_FLO_ANON_ID_MAPPED, "true");
          }
          return fingerprintId;
        }

        return existingId;
      } catch (error) {
        console.error("Error initializing anonymous ID:", error);
        return null;
      }
    },
    100,
  );

  /**
   * Called on logout. The device ID stays - it is the same device - but the next person to log in
   * here has to be mapped to it, so the marker goes.
   */
  const resetUserMapping = (): void => {
    localStorage.removeItem(constants.LOCAL_STORAGE_FLO_ANON_ID_MAPPED);
  };

  const resetAnonymousId = (): void => {
    clearAnonymousId();
    setAnonymousId(null);
  };

  return {
    anonymousId,
    initializeAnonymousId,
    resetUserMapping,
    resetAnonymousId,
  };
};

export default useAnonymousId;
